<script setup lang="ts">
import { nextTick, ref, onUnmounted, computed } from 'vue';

const props = withDefaults(defineProps<{
  containerId?: string;
  size?: 'invisible' | 'compact' | 'normal';
  theme?: 'light' | 'dark';
  badge?: 'bottomright' | 'bottomleft' | 'inline';
  tabindex?: number;
}>(), {
  containerId: '',
  size: 'invisible',
  theme: 'light',
  badge: 'bottomright',
  tabindex: 0
});

interface EmitsInterface {
  (event: 'captcha-success', token: string | null): void
  (event: 'captcha-loaded'): void
}

const emits = defineEmits<EmitsInterface>();
const recaptcha = ref();
const successCallbackKey = `captcha-success-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const disableGoogleRecaptcha = import.meta.env.VITE_APP_DISABLE_GOOGLE_RECAPTCHA === 'true';

(window as unknown as Record<string, (token: string) => void>)[successCallbackKey] = (token: string): void => {
  emits('captcha-success', token);
  emits('captcha-loaded');
};

const siteKey = import.meta.env.VITE_GOOGLE_CAPTCHA_SECRET;
const recaptchaWidgetId = ref<number | null>(null);

const shouldRenderDiv = computed(() => disableGoogleRecaptcha === true ? false : !props.containerId);

const ready = (callback: () => void): void => {
  if (window.grecaptcha) {
    window.grecaptcha.ready(callback);

    return;
  }
  (window.grecaptchaReadyQueue ??= []).push(callback);
};

window.onCaptchaLoaded = (): void => {
  window.grecaptcha.ready(() => {
    const callbacks = window.grecaptchaReadyQueue ?? [];
    window.grecaptchaReadyQueue = [];
    callbacks.forEach((callback) => callback());
  });
  emits('captcha-loaded');
};

const setScript = (): void => {
  if (disableGoogleRecaptcha) {
    return;
  }
  const alreadyLoaded = document.getElementById('recaptcha-script');

  if (alreadyLoaded) {
    return;
  }

  const script = document.createElement('script');
  script.id = 'recaptcha-script';
  script.src = 'https://www.google.com/recaptcha/api.js?onload=onCaptchaLoaded';
  script.async = true;
  script.defer = true;

  document.head.appendChild(script);
};

const renderRecaptcha = (): void => {
  if (disableGoogleRecaptcha) {
    return;
  }
  ready(() => {
    if (recaptchaWidgetId.value !== null) {
      return;
    }

    let container: HTMLElement | null;

    if (props.containerId) {
      container = document.getElementById(props.containerId);

      if (!container) {
        container = document.createElement('div');
        container.id = props.containerId;
        container.style.position = 'absolute';
        container.style.zIndex = '2001';
        document.body.appendChild(container);
      }
    } else {
      container = recaptcha.value;
    }

    if (!container) {
      return;
    }
    try {
      recaptchaWidgetId.value = window.grecaptcha.render(container, {
        sitekey: siteKey,
        size: props.size,
        theme: props.theme,
        badge: props.badge,
        tabindex: props.tabindex,
        callback: successCallbackKey
      });
    } catch {
      return;
    }
  });
};

const removeRecaptcha = (): void => {
  ready(() => {
    if (recaptchaWidgetId.value !== null) {
      try {
        window.grecaptcha.reset(recaptchaWidgetId.value);
      } catch {
        return;
      }

      if (props.containerId) {
        const container = document.getElementById(props.containerId);
        container?.remove();
      }

      recaptchaWidgetId.value = null;
    }
  });
};

const executeRecaptcha = async (): Promise<string | null> => {
  if (disableGoogleRecaptcha) {
    emits('captcha-success', 'disabled');

    return null;
  }
  if (props.containerId && recaptchaWidgetId.value === null) {
    return null;
  }

  return new Promise((resolve, reject) => {
    ready(() => {
      try {
        if (recaptchaWidgetId.value !== null) {
          window.grecaptcha.reset(recaptchaWidgetId.value);
        }
        window.grecaptcha.execute(recaptchaWidgetId.value ?? undefined)
          .then(resolve)
          .catch(reject);
      } catch (error) {
        reject(error);
      }
    });
  });
};

onUnmounted(() => {
  removeRecaptcha();
});

defineExpose({ executeRecaptcha, renderRecaptcha });

nextTick(() => {
  setScript();
});
</script>
<template>
  <div v-if="shouldRenderDiv" class="g-recaptcha"
    :data-sitekey="siteKey"
    :data-size="size"
    :data-theme="theme"
    :data-badge="badge"
    :data-tabindex="tabindex"
    :data-callback="successCallbackKey"
    ref="recaptcha">
  </div>
</template>
